import React, { useState, useEffect, useContext } from 'react';
import { DataContext } from '../../init/getData';
import { Form, Row, Col, Card, Button, Alert, Table, Tag, Spin, Tabs, Select, Checkbox, Input, Modal } from 'antd';
import { 
  PlayCircleOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  DatabaseOutlined,
  ShopOutlined,
  EyeOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import toast from 'react-hot-toast';

const { Option } = Select;

function StockClosing() {
  const { userData, selectedCompany, api, warehouseData } = useContext(DataContext);  
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [canClose, setCanClose] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [stockPreview, setStockPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  const [newYearForm] = Form.useForm();

  useEffect(() => {
    loadYears();
    loadWarehousesFromContext();
  }, [selectedCompany, warehouseData]);

  const loadYears = async () => {
    if (!selectedCompany?.id) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/year/getYears?companyId=${selectedCompany.id}`);
      if (response.data.status) {
        setYears(response.data.data);
      }
    } catch (error) {
      console.error('Error loading years:', error);
      toast.error('Failed to load years');
    } finally {
      setLoading(false);
    }
  };

  const loadWarehousesFromContext = () => {
    if (!selectedCompany?.id || !warehouseData) return;
    
    try {
      // Filter warehouses for the current company
      const filteredWarehouses = warehouseData.filter(warehouse => 
        warehouse.companyId === selectedCompany.id && warehouse.isActive
      );
      
      setWarehouses(filteredWarehouses);
      console.log('Loaded warehouses from context:', filteredWarehouses);
    } catch (error) {
      console.error('Error loading warehouses from context:', error);
      toast.error('Failed to load warehouses from context');
    }
  };

  const loadStockClosingPreview = async () => {
    if (!selectedYear || !selectedCompany?.id) return;
    
    setLoadingPreview(true);
    try {
      const response = await api.get(`/year/getStockClosingPreview`, {
        params: {
          companyId: selectedCompany.id,
          yearId: selectedYear.id,
          warehouseIds: selectedWarehouses.join(',')
        }
      });
      
      if (response.data.status) {
        setStockPreview(response.data.data);
        setShowPreview(true);
        
        // Set form values
        const { suggestedNewYear } = response.data.data;
        newYearForm.setFieldsValue({
          newYear: suggestedNewYear.year,
          startDate: suggestedNewYear.startDate,
          endDate: suggestedNewYear.endDate
        });
      } else {
        toast.error(response.data.message || 'Failed to load preview');
      }
    } catch (error) {
      console.error('Error loading stock preview:', error);
      toast.error(error.response?.data?.message || 'Failed to load stock preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const checkCanClose = async (yearId) => {
    try {
      const response = await api.get(`/year/canCloseYear?yearId=${yearId}`);
      if (response.data.status) {
        setCanClose(response.data.canClose);
      }
    } catch (error) {
      console.error('Error checking year:', error);
    }
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setSelectedWarehouses([]); // Reset warehouses
    setStockPreview(null);
    setShowPreview(false);
    checkCanClose(year.id);
  };

  const handleWarehouseChange = (selectedValues) => {
    setSelectedWarehouses(selectedValues);
    setStockPreview(null);
    setShowPreview(false);
  };

  const selectAllWarehouses = () => {
    setSelectedWarehouses(warehouses.map(w => w.id));
  };

  const clearAllWarehouses = () => {
    setSelectedWarehouses([]);
  };

  const closeStock = async () => {
    if (!selectedYear) return;

    // Validate at least one warehouse is selected
    if (selectedWarehouses.length === 0) {
      toast.error('Please select at least one warehouse');
      return;
    }

    const values = await newYearForm.validateFields();
    
    setClosing(true);
    try {
      const response = await api.post('/year/closeStock', {
        yearId: selectedYear.id,
        newYearData: {
          year: values.newYear,
          startDate: values.startDate,
          endDate: values.endDate,
          companyId: selectedCompany.id
        },
        userId: userData.id,
        warehouseIds: selectedWarehouses
      });

      if (response.data.status) {
        toast.success('Stock closed successfully!');
        
        // Reset form and state
        newYearForm.resetFields();
        setSelectedYear(null);
        setSelectedWarehouses([]);
        setStockPreview(null);
        setShowPreview(false);
        setPreviewModalVisible(false);
        loadYears(); // Reload years list
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error closing stock:', error);
      toast.error(error.response?.data?.message || 'Failed to close stock');
    } finally {
      setClosing(false);
    }
  };

  // Add a refresh button for warehouses if needed
  const refreshWarehouses = () => {
    loadWarehousesFromContext();
  };

  const columns = [
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      render: (text) => <span style={{ fontWeight: 'bold' }}>{text}</span>
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate'
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate'
    },
    {
      title: 'Status',
      dataIndex: 'isClosed',
      key: 'status',
      render: (isClosed) => (
        <Tag color={isClosed ? 'red' : 'green'}>
          {isClosed ? 'Closed' : 'Open'}
        </Tag>
      )
    },
    {
      title: 'Transactions',
      dataIndex: ['_count', 'journalVouchers'],
      key: 'transactions',
      render: (count) => <Tag color="blue">{count}</Tag>
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          disabled={record.isClosed}
          onClick={() => handleYearSelect(record)}
          icon={<DatabaseOutlined />}
        >
          Close Stock
        </Button>
      )
    }
  ];

  const warehouseColumns = [
    {
      title: 'Warehouse',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShopOutlined />
          <span>{name}</span>
          <Tag color={selectedWarehouses.includes(record.id) ? 'success' : 'default'}>
            {selectedWarehouses.includes(record.id) ? 'Selected' : 'Not Selected'}
          </Tag>
        </div>
      )
    },
    {
      title: 'Company',
      dataIndex: ['company', 'name'],
      key: 'company',
      render: (companyName) => companyName || '-'
    },
    {
      title: 'Account',
      dataIndex: ['account', 'english'],
      key: 'account',
      render: (accountName) => accountName || '-'
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      render: (details) => details || '-'
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Tag color={record.isActive ? 'green' : 'red'}>
          {record.isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    }
  ];

  const previewColumns = [
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category'
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity) => (
        <Tag color="blue">{parseFloat(quantity).toFixed(2)}</Tag>
      )
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit'
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      render: (cost) => `$${parseFloat(cost).toFixed(2)}`
    },
    {
      title: 'Total Value',
      dataIndex: 'totalValue',
      key: 'totalValue',
      render: (value) => (
        <Tag color="green">${parseFloat(value).toFixed(2)}</Tag>
      )
    }
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card 
            title="Year Management" 
            loading={loading}
            extra={
              <Button 
                type="text" 
                icon={<FileTextOutlined />} 
                onClick={refreshWarehouses}
              >
                Refresh Warehouses
              </Button>
            }
          >
            <Table
              columns={columns}
              dataSource={years}
              rowKey="id"
              pagination={false}
            />
          </Card>
        </Col>

        {selectedYear && (
          <Col span={24}>
            <Card 
              title={`Stock Closing: ${selectedYear.year}`}
              extra={
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => setPreviewModalVisible(true)}
                    disabled={!stockPreview}
                  >
                    View Details
                  </Button>
                  <Button
                    type="text"
                    icon={<CloseCircleOutlined />}
                    onClick={() => {
                      setSelectedYear(null);
                      setSelectedWarehouses([]);
                      setStockPreview(null);
                      setShowPreview(false);
                      newYearForm.resetFields();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              }
            >
              {!canClose && (
                <Alert
                  message="Cannot Close Year"
                  description="Please ensure all sessions are closed before proceeding with stock closing."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <Row gutter={16}>
                <Col span={12}>
                  <Card title="Warehouse Selection" size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                      <Col span={12}>
                        <Select
                          mode="multiple"
                          style={{ width: '100%' }}
                          placeholder="Select warehouses to transfer stock from..."
                          value={selectedWarehouses}
                          onChange={handleWarehouseChange}
                          loading={!warehouses.length}
                        >
                          {warehouses.map(warehouse => (
                            <Option key={warehouse.id} value={warehouse.id}>
                              {warehouse.name} ({warehouse.company?.name || 'No Company'})
                            </Option>
                          ))}
                        </Select>
                      </Col>
                      <Col span={12}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button size="small" onClick={selectAllWarehouses}>
                            Select All
                          </Button>
                          <Button size="small" onClick={clearAllWarehouses}>
                            Clear All
                          </Button>
                          <Button 
                            type="primary" 
                            size="small"
                            onClick={loadStockClosingPreview}
                            loading={loadingPreview}
                            disabled={selectedWarehouses.length === 0}
                          >
                            Load Preview
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    <Alert
                      message="Warehouse Information"
                      description={`${warehouses.length} warehouses available for company ${selectedCompany?.name || 'Unknown'}`}
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                    />

                    <Table
                      columns={warehouseColumns}
                      dataSource={warehouses}
                      rowKey="id"
                      size="small"
                      pagination={false}
                      scroll={{ y: 200 }}
                    />
                  </Card>

                  {showPreview && (
                    <Card title="New Year Configuration" size="small">
                      <Form
                        form={newYearForm}
                        layout="vertical"
                        onFinish={closeStock}
                      >
                        <Row gutter={16}>
                          <Col span={8}>
                            <Form.Item
                              label="New Year"
                              name="newYear"
                              rules={[{ required: true, message: 'Please enter new year' }]}
                            >
                              <Input 
                                placeholder="e.g., 2024"
                                type="number"
                                min={parseInt(selectedYear.year) + 1}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              label="Start Date"
                              name="startDate"
                              rules={[{ required: true, message: 'Please select start date' }]}
                            >
                              <Input type="date" />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              label="End Date"
                              name="endDate"
                              rules={[{ required: true, message: 'Please select end date' }]}
                            >
                              <Input type="date" />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Alert
                          message="Stock Transfer Information"
                          description={`Stock will be transferred from ${selectedWarehouses.length} warehouses to the new year.`}
                          type="info"
                          showIcon
                          style={{ marginBottom: 16 }}
                        />

                        <Form.Item>
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={closing}
                            disabled={!canClose || selectedWarehouses.length === 0}
                            icon={<CheckCircleOutlined />}
                            size="large"
                          >
                            Close Stock for {selectedYear.year} and Open New Year
                          </Button>
                          {selectedWarehouses.length === 0 && (
                            <div style={{ marginTop: 8, color: '#f5222d' }}>
                              Please select at least one warehouse
                            </div>
                          )}
                        </Form.Item>
                      </Form>
                    </Card>
                  )}
                </Col>

                <Col span={12}>
                  {loadingPreview ? (
                    <Card title="Loading Preview" size="small">
                      <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin size="large" />
                        <p>Loading stock data...</p>
                      </div>
                    </Card>
                  ) : stockPreview ? (
                    <Card 
                      title="Stock Transfer Preview" 
                      size="small"
                      extra={
                        <Tag color="blue">
                          {stockPreview.summary.totalWarehouses} Warehouses
                        </Tag>
                      }
                    >
                      {stockPreview.warehouseStockData.map((warehouse, index) => (
                        <div 
                          key={warehouse.warehouseId} 
                          style={{ 
                            marginBottom: 12, 
                            padding: 12, 
                            border: '1px solid #f0f0f0',
                            borderRadius: 6
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <ShopOutlined style={{ color: '#1890ff' }} />
                              <div>
                                <div style={{ fontWeight: 'bold' }}>{warehouse.warehouseName}</div>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                  {warehouse.totalProducts} products • {warehouse.totalQuantity.toFixed(2)} units
                                </div>
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 'bold', fontSize: 16 }}>
                                ${warehouse.totalValue.toFixed(2)}
                              </div>
                              <div style={{ fontSize: 12, color: '#666' }}>
                                Total Value
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      <div style={{ 
                        marginTop: 16, 
                        padding: 16, 
                        background: '#f6ffed', 
                        borderRadius: 8,
                        border: '1px solid #b7eb8f'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span><strong>Total Warehouses:</strong></span>
                          <span><strong>{stockPreview.summary.totalWarehouses}</strong></span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span><strong>Total Products:</strong></span>
                          <span><strong>{stockPreview.summary.totalProducts}</strong></span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span><strong>Total Quantity:</strong></span>
                          <span><strong>{stockPreview.summary.totalQuantity.toFixed(2)}</strong></span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span><strong>Total Value:</strong></span>
                          <span><strong>${stockPreview.summary.totalValue.toFixed(2)}</strong></span>
                        </div>
                      </div>
                    </Card>
                  ) : (
                    <Card title="Stock Preview" size="small">
                      <div style={{ textAlign: 'center', padding: 40 }}>
                        <DatabaseOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                        <p>Select warehouses and click "Load Preview" to see stock data</p>
                        <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                          Available warehouses: {warehouses.length}
                        </div>
                      </div>
                    </Card>
                  )}
                </Col>
              </Row>
            </Card>
          </Col>
        )}
      </Row>

      {/* Preview Modal */}
      <Modal
        title="Stock Transfer Details"
        visible={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={null}
        width={1000}
      >
        {stockPreview && stockPreview.warehouseStockData.map((warehouse) => (
          <Card
            key={warehouse.warehouseId}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ShopOutlined />
                {warehouse.warehouseName}
              </div>
            }
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              <div>
                <Tag color="blue">{warehouse.totalProducts} products</Tag>
                <Tag color="green">${warehouse.totalValue.toFixed(2)}</Tag>
              </div>
            }
          >
            <Table
              columns={previewColumns}
              dataSource={warehouse.products}
              rowKey="productId"
              size="small"
              pagination={{ pageSize: 5 }}
            />
          </Card>
        ))}
      </Modal>
    </div>
  );
}

export default StockClosing;